<template lang="pug">
div(class="orders-calendar")
  .calendar-header
    .calendar-controls
      button.btn.btn-sm.btn-outline-primary(@click="previousPeriod")
        i.fa-solid.fa-chevron-left
      h4.current-period {{currentPeriodLabel}}
      button.btn.btn-sm.btn-outline-primary(@click="nextPeriod")
        i.fa-solid.fa-chevron-right

    .view-controls
      .btn-group(role="group")
        button.btn.btn-sm(:class="viewType === 'month' ? 'btn-primary' : 'btn-outline-primary'" @click="setView('month')") {{$I18n.t('vue.calendar.month')}}
        button.btn.btn-sm(:class="viewType === 'week' ? 'btn-primary' : 'btn-outline-primary'" @click="setView('week')") {{$I18n.t('vue.calendar.week')}}
        button.btn.btn-sm(:class="viewType === 'day' ? 'btn-primary' : 'btn-outline-primary'" @click="setView('day')") {{$I18n.t('vue.calendar.day')}}

    .filter-controls
      select.form-control.form-control-sm(v-model="statusFilter" @change="loadEvents")
        option(value="") {{$I18n.t('vue.calendar.all_statuses')}}
        option(value="REGISTERED") {{$I18n.t('vue.states.registered')}}
        option(value="SCHEDULED") {{$I18n.t('vue.states.scheduled')}}
        option(value="COMPLETED") {{$I18n.t('vue.states.completed')}}
        option(value="CANCELED") {{$I18n.t('vue.states.canceled')}}

  .calendar-body
    table.table.table-bordered.calendar-table(v-if="viewType === 'month'")
      thead
        tr
          th(v-for="day in weekDays" :key="day") {{$I18n.t('vue.calendar.days.' + day)}}
      tbody
        tr(v-for="(week, weekIndex) in calendarWeeks" :key="`week-${weekIndex}`")
          td(
            v-for="(day, dayIndex) in week" 
            :key="`day-${weekIndex}-${dayIndex}-${day.date}`"
            :class="{'other-month': !day.currentMonth, 'today': day.isToday}"
            @click="selectDay(day)"
          )
            .day-header
              .day-number {{day.dayNumber}}
              .event-count(v-if="day.events.length > 3")
                small.text-muted +{{day.events.length - 3}}
            .day-events
              .event(
                v-for="(event, eventIndex) in day.events"
                :key="event.id"
                v-show="eventIndex < 3"
                :style="{backgroundColor: event.backgroundColor}"
                @click.stop="viewOrder(event)"
                :title="event.title"
              )
                small {{event.title}}

    .week-view(v-if="viewType === 'week'")
      .time-slot(v-for="event in sortedEvents" :key="event.id")
        .event-card(:style="{borderLeft: '4px solid ' + event.backgroundColor}")
          .event-time {{formatDate(event.start)}}
          .event-title {{event.title}}
          .event-details
            small.text-muted {{event.extendedProps.customer}}
          button.btn.btn-sm.btn-link(@click="viewOrder(event)") {{$I18n.t('vue.calendar.view')}}

    .day-view(v-if="viewType === 'day'")
      .event-list
        .event-item(
          v-for="event in sortedEvents"
          :key="event.id"
          :style="{borderLeft: '4px solid ' + event.backgroundColor}"
        )
          .event-header
            strong {{event.title}}
            span.badge.ms-2(:style="{backgroundColor: event.backgroundColor}") {{event.extendedProps.status}}
          .event-body
            p.mb-1
              i.fa-solid.fa-building.me-2
              | {{event.extendedProps.subsidary}}
            p.mb-1
              i.fa-solid.fa-user.me-2
              | {{event.extendedProps.customer}}
            p.mb-1(v-if="event.extendedProps.technicians")
              i.fa-solid.fa-users.me-2
              | {{event.extendedProps.technicians}}
            p.mb-0
              i.fa-solid.fa-dollar-sign.me-2
              | ${{event.extendedProps.price}}
          .event-actions
            a.btn.btn-sm.btn-primary(:href="event.url") {{$I18n.t('vue.calendar.view_details')}}

  .calendar-footer
    p.text-muted.small
      | {{$I18n.t('vue.calendar.total_orders')}}: {{events.length}}
</template>

<script>
const { BackendService } = require('app/services');

export default {
  data() {
    return {
      viewType: 'month',
      currentDate: new Date(),
      events: [],
      statusFilter: '',
      weekDays: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    };
  },
  computed: {
    currentLocale() {
      // Get locale from I18n (es or en)
      return this.$I18n.locale === 'es' ? 'es-ES' : 'en-US';
    },
    currentPeriodLabel() {
      const locale = this.currentLocale;
      const options = { year: 'numeric', month: 'long' };
      if (this.viewType === 'week') {
        const weekStart = this.getWeekStart(this.currentDate);
        const weekEnd = this.getWeekEnd(this.currentDate);
        return `${weekStart.toLocaleDateString(locale)} - ${weekEnd.toLocaleDateString(locale)}`;
      } else if (this.viewType === 'day') {
        return this.currentDate.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      }
      return this.currentDate.toLocaleDateString(locale, options);
    },
    calendarWeeks() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const weeks = [];
      let currentWeek = [];
      
      // Pad beginning
      const firstDayOfWeek = firstDay.getDay();
      for (let i = 0; i < firstDayOfWeek; i++) {
        const date = new Date(year, month, -firstDayOfWeek + i + 1);
        currentWeek.push(this.createDayObject(date, false));
      }
      
      // Fill month days
      for (let day = 1; day <= lastDay.getDate(); day++) {
        const date = new Date(year, month, day);
        currentWeek.push(this.createDayObject(date, true));
        
        if (currentWeek.length === 7) {
          weeks.push(currentWeek);
          currentWeek = [];
        }
      }
      
      // Pad ending
      while (currentWeek.length < 7 && currentWeek.length > 0) {
        const date = new Date(year, month + 1, currentWeek.length - 6);
        currentWeek.push(this.createDayObject(date, false));
      }
      
      if (currentWeek.length > 0) {
        weeks.push(currentWeek);
      }
      
      return weeks;
    },
    sortedEvents() {
      return [...this.events].sort((a, b) => new Date(a.start) - new Date(b.start));
    }
  },
  mounted() {
    this.loadEvents();
  },
  methods: {
    createDayObject(date, currentMonth) {
      const dateStr = date.toISOString().split('T')[0];
      const dayEvents = this.events.filter(e => e.start === dateStr);
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();
      
      return {
        date: dateStr,
        dayNumber: date.getDate(),
        currentMonth,
        isToday,
        events: dayEvents
      };
    },
    loadEvents() {
      const params = {
        view: this.viewType,
        date: this.currentDate.toISOString().split('T')[0]
      };
      
      if (this.statusFilter) {
        params.status = this.statusFilter;
      }
      
      // BackendService.get() acepta params directamente, no anidados
      BackendService.get('/calendar.json', params)
        .then(response => {
          // BackendService.get() ya parsea JSON, response ES el objeto data
          this.events = response.events || [];
        })
        .catch(error => {
          console.error('Error loading calendar events:', error);
        });
    },
    setView(type) {
      this.viewType = type;
      this.loadEvents();
    },
    previousPeriod() {
      if (this.viewType === 'month') {
        this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
      } else if (this.viewType === 'week') {
        this.currentDate = new Date(this.currentDate.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else {
        this.currentDate = new Date(this.currentDate.getTime() - 24 * 60 * 60 * 1000);
      }
      this.loadEvents();
    },
    nextPeriod() {
      if (this.viewType === 'month') {
        this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
      } else if (this.viewType === 'week') {
        this.currentDate = new Date(this.currentDate.getTime() + 7 * 24 * 60 * 60 * 1000);
      } else {
        this.currentDate = new Date(this.currentDate.getTime() + 24 * 60 * 60 * 1000);
      }
      this.loadEvents();
    },
    selectDay(day) {
      this.currentDate = new Date(day.date);
      this.setView('day');
    },
    viewOrder(event) {
      window.location.href = event.url;
    },
    getWeekStart(date) {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day;
      return new Date(d.setDate(diff));
    },
    getWeekEnd(date) {
      const start = this.getWeekStart(date);
      return new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.orders-calendar {
  background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(139, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid rgba(139, 0, 0, 0.06);
}

.calendar-body {
  overflow-x: auto;
  max-width: 100%;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 2px solid rgba(139, 0, 0, 0.1);
  flex-wrap: wrap;
  gap: 15px;
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.calendar-controls button {
  background: linear-gradient(135deg, #8B0000 0%, #A52A2A 100%);
  border: none;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(139, 0, 0, 0.15);
}

.calendar-controls button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(139, 0, 0, 0.25);
  background: linear-gradient(135deg, #A52A2A 0%, #8B0000 100%);
}

.current-period {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3436;
  letter-spacing: -0.5px;
}

.calendar-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid rgba(139, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}

.calendar-table th {
  text-align: center;
  padding: 14px 10px;
  background: linear-gradient(135deg, #8B0000 0%, #A52A2A 100%);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: 14.28%;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.calendar-table th:last-child {
  border-right: none;
}

.calendar-table td {
  height: 120px;
  vertical-align: top;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 14.28%;
  overflow: hidden;
  background-color: #ffffff;
  border-right: 1px solid rgba(139, 0, 0, 0.08);
  border-bottom: 1px solid rgba(139, 0, 0, 0.08);
  position: relative;
}

.calendar-table td:hover {
  background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%);
  box-shadow: inset 0 0 0 2px rgba(139, 0, 0, 0.15);
}

.calendar-table td.other-month {
  background-color: #f8f9fa;
  color: #adb5bd;
}

.calendar-table td.other-month:hover {
  background: linear-gradient(135deg, #f1f3f5 0%, #f8f9fa 100%);
}

.calendar-table td.today {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
  box-shadow: inset 0 0 0 2px rgba(139, 0, 0, 0.2);
}

.calendar-table td.today .day-number {
  background: linear-gradient(135deg, #8B0000 0%, #A52A2A 100%);
  color: white;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 700;
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(139, 0, 0, 0.08);
}

.day-number {
  font-weight: 700;
  font-size: 0.95rem;
  color: #2d3436;
}

.event-count {
  font-size: 0.65rem;
  color: #8B0000;
  background: linear-gradient(135deg, #ffe5e5 0%, #fff5f5 100%);
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  border: 1px solid rgba(139, 0, 0, 0.15);
}

.day-events {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 100px;
  overflow-y: auto;
}

.event {
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  color: white;
  cursor: pointer;
  white-space: normal;
  word-wrap: break-word;
  overflow: hidden;
  line-height: 1.3;
  max-height: 2.6em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  font-weight: 500;
  border-left: 3px solid rgba(255, 255, 255, 0.5);
}

.event:hover {
  transform: translateX(2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
}

.week-view, .day-view {
  max-height: 600px;
  overflow-y: auto;
  padding: 16px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid rgba(139, 0, 0, 0.1);
}

.event-card, .event-item {
  background: linear-gradient(to right, #ffffff 0%, #fafafa 100%);
  border: 1px solid rgba(139, 0, 0, 0.12);
  border-radius: 8px;
  padding: 18px;
  margin-bottom: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.event-card:hover, .event-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(139, 0, 0, 0.12);
  border-color: rgba(139, 0, 0, 0.25);
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 2px solid rgba(139, 0, 0, 0.08);
}

.event-header strong {
  color: #2d3436;
  font-size: 1.05rem;
  font-weight: 700;
}

.event-body p {
  margin: 0;
  padding: 8px 0;
  color: #636e72;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.event-body i {
  color: #8B0000;
  width: 24px;
}

.event-actions {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(139, 0, 0, 0.08);
  display: flex;
  justify-content: flex-end;
}

.event-actions .btn-primary {
  background: linear-gradient(135deg, #8B0000 0%, #A52A2A 100%);
  border: none;
  padding: 8px 20px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(139, 0, 0, 0.2);
}

.event-actions .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(139, 0, 0, 0.3);
  background: linear-gradient(135deg, #A52A2A 0%, #8B0000 100%);
}

.calendar-footer {
  margin-top: 24px;
  padding-top: 20px;
  text-align: center;
  border-top: 2px solid rgba(139, 0, 0, 0.1);
}

.calendar-footer p {
  color: #636e72;
  font-weight: 600;
  font-size: 0.95rem;
}

/* Responsividad para pantallas pequeñas */
@media (max-width: 1200px) {
  .calendar-table td {
    height: 100px;
    font-size: 0.85rem;
    padding: 6px;
  }
  
  .event {
    font-size: 0.65rem;
    padding: 5px 6px;
  }
  
  .day-number {
    font-size: 0.85rem;
  }

  .calendar-table th {
    padding: 12px 8px;
    font-size: 0.85rem;
  }
}

@media (max-width: 768px) {
  .orders-calendar {
    padding: 16px;
    border-radius: 8px;
  }
  
  .calendar-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding-bottom: 16px;
  }
  
  .calendar-controls,
  .view-controls,
  .filter-controls {
    width: 100%;
    justify-content: center;
  }
  
  .calendar-table td {
    height: 80px;
    padding: 4px;
  }
  
  .event {
    font-size: 0.6rem;
    padding: 4px 6px;
    max-height: 1.8em;
    -webkit-line-clamp: 1;
    line-clamp: 1;
  }
  
  .day-events {
    max-height: 60px;
    gap: 3px;
  }
  
  .day-number {
    font-size: 0.75rem;
    margin-bottom: 2px;
  }

  .calendar-table th {
    padding: 10px 6px;
    font-size: 0.75rem;
  }

  .current-period {
    font-size: 1.1rem;
  }
}

/* Mejora del scroll en contenedores de eventos */
.day-events::-webkit-scrollbar {
  width: 4px;
}

.day-events::-webkit-scrollbar-track {
  background: rgba(139, 0, 0, 0.05);
  border-radius: 4px;
}

.day-events::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #8B0000 0%, #A52A2A 100%);
  border-radius: 4px;
}

.day-events::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #A52A2A 0%, #8B0000 100%);
}

/* Estilos para botones de vista */
.view-controls .btn-group {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  overflow: hidden;
}

.view-controls .btn-primary {
  background: linear-gradient(135deg, #8B0000 0%, #A52A2A 100%);
  border-color: #8B0000;
  font-weight: 600;
}

.view-controls .btn-outline-primary {
  color: #8B0000;
  border-color: rgba(139, 0, 0, 0.3);
  background: white;
  font-weight: 500;
  transition: all 0.2s ease;
}

.view-controls .btn-outline-primary:hover {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
  border-color: #8B0000;
  color: #8B0000;
}

/* Estilos para filtros */
.filter-controls select {
  border: 2px solid rgba(139, 0, 0, 0.15);
  border-radius: 6px;
  padding: 8px 12px;
  font-weight: 500;
  color: #2d3436;
  transition: all 0.2s ease;
  background-color: white;
}

.filter-controls select:focus {
  border-color: #8B0000;
  box-shadow: 0 0 0 3px rgba(139, 0, 0, 0.1);
  outline: none;
}

/* Estilos adicionales para badges y detalles */
.badge {
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

/* Estilos para la vista de semana */
.week-view .event-card {
  position: relative;
  overflow: hidden;
}

.week-view .event-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #8B0000 0%, #A52A2A 100%);
}

.week-view .event-time {
  color: #8B0000;
  font-weight: 700;
  font-size: 0.85rem;
  margin-bottom: 6px;
}

.week-view .event-title {
  color: #2d3436;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.week-view .event-details {
  color: #636e72;
  margin-bottom: 10px;
}

.week-view .btn-link {
  color: #8B0000;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.week-view .btn-link:hover {
  color: #A52A2A;
  text-decoration: underline;
}

/* Animaciones sutiles */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.event-card, .event-item {
  animation: fadeIn 0.3s ease-out;
}
</style>
